import { ar, direction, en, type Lang } from '@dob/i18n';
import i18next from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';

import { desktop } from './platform.ts';

declare module 'i18next' {
  interface CustomTypeOptions {
    resources: { translation: typeof ar };
  }
}

const STORAGE_KEY = 'dob.lang';

function savedLang(): Lang {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'en' ? 'en' : 'ar';
  } catch {
    return 'ar';
  }
}

/** Sets `<html lang dir>` so the whole layout flips between RTL (Arabic) and LTR (English). */
function applyToDocument(lang: Lang): void {
  document.documentElement.lang = lang;
  document.documentElement.dir = direction(lang);
  document.title = `${i18next.t('app.name')} — ${i18next.t('app.product')}`;
}

export async function initI18n(): Promise<void> {
  await i18next.use(initReactI18next).init({
    resources: { ar: { translation: ar }, en: { translation: en } },
    lng: savedLang(),
    fallbackLng: 'ar',
    // React already escapes output.
    interpolation: { escapeValue: false },
    returnNull: false,
  });
  applyToDocument(currentLang());
}

export function currentLang(): Lang {
  return i18next.language === 'en' ? 'en' : 'ar';
}

export async function setLang(lang: Lang): Promise<void> {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // Private mode: the choice lasts for this visit only.
  }
  await i18next.changeLanguage(lang);
  applyToDocument(lang);
  // Dob Desktop keeps the language too, for its own messages (e.g. the hub's shutdown warning).
  if (desktop) void desktop.setup.setLanguage(lang);
}

/** `t` plus the current language, for data that comes in both languages from the API. */
export function useLang() {
  const { t, i18n } = useTranslation();
  const lang: Lang = i18n.language === 'en' ? 'en' : 'ar';
  /** Picks the Arabic or English value of a bilingual field (falls back to Arabic). */
  const pick = (arValue: string, enValue: string | null | undefined) =>
    lang === 'en' && enValue ? enValue : arValue;
  return { t, lang, pick };
}
